import { Dictionary, Skyward, SkywardErrorStatic, DataGathererCallback  } from "./types";
import type * as VueType from "vue";

declare global
{
    interface Performance
    {
        // chrome extension
        memory?:
        {
            jsHeapSize: number;
            usedJsHeapSize: number;
            jsHeapSizeLimit: number;
        }
    }
    var Vue: typeof VueType;
}

export class SkywardException extends Error
{
    details?: string;
    severity: Severity = Severity.Error;
    applicationMonitoringIdentifier?: string;

    constructor(
        message: string,
        details?: string,
        severity?: Severity,
        applicationMonitoringIdentifier?: string
    )
    {
        super(message);
        this.details = details;
        this.applicationMonitoringIdentifier = applicationMonitoringIdentifier;
        if (severity)
        {
            this.severity = severity;
        }
    }
}

export enum Severity
{
    Debug = "Debug",
    Info = "Info",
    Error = "Error",
    SupportInfo = "SupportInfo"
}

interface InternalErrorStatic extends SkywardErrorStatic
{
    _enableLogging: boolean;
}

type BasicErrorData = {
    message: string;
    location?: string;
    stack?: string;
}

type AggregateErrorData = (BasicErrorData & { innerErrors: AggregateErrorData | undefined; })[];

// If these appear in a stack trace, we can be certain the error came from an extension or from running code in the console.
const LOCATIONS_TO_IGNORE = [
    /debugger eval code/,
    /evaluateWithScopeExtension@[native code]/,
    /extension:\/\//,
];
// Based on https://github.com/getsentry/sentry-javascript/blob/master/packages/core/src/integrations/inboundfilters.ts
// "Script error." is hard coded into browsers for errors that it can't read.
// this is the result of a script being pulled in from an external domain and CORS.
const ERROR_MESSAGES_TO_IGNORE = [
      /^Script error\.?$/,
      /^Javascript error: Script error\.? on line 0$/,
      /^Cannot redefine property: googletag$/, // This is thrown when google tag manager is used in combination with an ad blocker
      /undefined is not an object \(evaluating 'a.L'\)/, // Random error that happens but not actionable or noticeable to end-users.
    ];

let errorNumber = 0;
const dataGatherers: Dictionary<string, DataGathererCallback> = Object.create(null);

const seenErrors: Dictionary<string, number> = Object.create(null);
let errorTimes: Date[] = [];

function shouldIgnoreErrorEvent(error: ErrorEvent)
{
    // Default values if there is no error attached to the event
    if (error.lineno === 0 && error.colno === 0 ||
        error.filename === "" || error.message === "Script error.")
    {
        return true;
    }
    return false;
}

function getBasicErrorData(errorArg: any, messageArg?: any, locationArg?: any): BasicErrorData
{
    const error = errorArg instanceof Error ? errorArg : { message: String(errorArg), stack: undefined };
    const data =
    {
        message: messageArg ?? error?.message,
        location: locationArg ?? getLocationFromStack(error?.stack),
        stack: error?.stack?.toString(),
    };
    return data;
}

function shouldIgnoreError(message: string, location: string, error: any)
{
    if (!(window.skyward.error as InternalErrorStatic)._enableLogging)
    {
        return true;
    }
    if (typeof error?.stack === "string" && LOCATIONS_TO_IGNORE.some(location => error.stack.indexOf(location) !== -1))
    {
        return true;
    }
    else if (typeof message === "string" && ERROR_MESSAGES_TO_IGNORE.some(m => m.test(message)))
    {
        return true;
    }
    else if (typeof message === "string" && typeof error?.stack === "string" &&
        message === "TypeError: Failed to fetch" &&
        /^at L6b \(<anonymous>:\d+:\d+\)\s*at m_u \(<anonymous>:\d+:\d+\)\s*at W9 \(<anonymous>:\d+:\d+\)\s*at <anonymous>:\d+:\d+$/.test(error.stack))
    {
        return true;
    }
    const stack = error === null || error === undefined ? undefined : error.stack;
    const locationForKey = refineLocation(location);
    const key = `${locationForKey}\n${message}\n${String(error)}\n${stack}`;
    const seenCount = seenErrors[key] = (seenErrors[key] ?? 0) + 1;
    if (seenCount > 3)
    {
        return true;
    }
    const startTime = new Date(Date.now() - 60 * 1000);
    errorTimes = errorTimes.filter(time => time >= startTime);
    if (errorTimes.length >= 10)
    {
        return true;
    }
    if (errorNumber >= 1000)
    {
        return true;
    }
    return false;
}

function escapeForHtml(value: string)
{
    const span = document.createElement("span");
    span.textContent = value;
    return span.innerHTML;
}

function nullPromise()
{
    return { then: nullPromise };
}

function resolvedPromise<T>(value: T)
{
    return window.Promise?.resolve(value) ?? window.jQuery?.Deferred().resolve(value) ?? nullPromise();
}

function rejectedPromise<T>(value: T)
{
    return window.Promise?.reject(value) ?? window.jQuery?.Deferred().reject(value) ?? nullPromise();
}

function withPromise<T>(callback: (resolve: (value: T | PromiseLike<T>) => void, reject: (reason?: any) => void) => void): PromiseLike<T>
{
    if (window.Promise)
    {
        return new Promise(callback);
    }
    else if (window.jQuery?.Deferred)
    {
        const deferred = window.jQuery.Deferred();
        callback(deferred.resolve.bind(deferred), deferred.reject.bind(deferred));
        return deferred.promise();
    }
    else
    {
        callback((_) => { }, (_) => { });
        return nullPromise();
    }
}

function gatherDetails(error: any)
{
    const data: Dictionary<string, any> = {};
    for (let key in dataGatherers)
    {
        try
        {
            const value = dataGatherers[key]!(error);
            JSON.stringify(value); // make sure it can be stringified before including it
            data[key] = value;
        }
        catch (e)
        {
            data[key] = `Error while gathering data: ${e}\n${e.stack}`;
        }
    }
    return data;
}

export function registerErrorDataGatherer(name: string, callback: DataGathererCallback)
{
    if (dataGatherers[name])
    {
        console.warn(`There is already an error data gatherer registered as "${name}"`);
        return;
    }
    dataGatherers[name] = callback;
}

/**
 * Method used to report an exception from the Client-Side.
 * 
 * @param exception
 * @param applicationMonitoringIdentifierParts A list of strings used to call Log.ComputeApplicationMonitoringIdentifier() 
 *                                             on the server-side to compute a Guid for the ApplicationMonitoringIdentifier.
 * @returns
 */
export function reportException(exception: Error, applicationMonitoringIdentifierParts?: string[])
{
    // make sure not to add insult to injury
    try
    {
        return reportExceptionInternal(exception, undefined, applicationMonitoringIdentifierParts);
    }
    catch (e)
    {
        return rejectedPromise(null);
    }
}

function getLocationFromStackInternal(frames: string[], ignoredSources: string[] = [])
{
    const preferenceKey = function (frame: string)
    {
        // This file is served separately, so bundling isn't an issue
        // the error.ts file is not helpful in determining the source of the error, so it should be a low preference
        if (frame.indexOf("skyward.base.error.") !== -1)
        {
            return 4;
        }
        // Ignored sources are purposely ignored
        if (ignoredSources.some(source => frame.indexOf(source) !== -1))
        {
            return 3;
        }
        // This frame does not contain a script file, so it should be our lowest preference
        if (frame.indexOf(".ts") === -1 && frame.indexOf(".js") === -1)
        {
            return 5;
        }
        // This frame doesn't contain skyward, so it's a 3rd party library
        if (frame.indexOf("skyward") === -1)
        {
            if (frame.indexOf(".ts") !== -1 || frame.indexOf(".js") !== -1)
            {
                return 1;
            }

            return 2;            
        }
        // If no conditions are met, this is our first preference
        return 0;
    }
    let minKey = Infinity;
    let minFrame: string = "";
    for (let frame of frames)
    {
        const key = preferenceKey(frame);
        if (key < minKey)
        {
            minKey = key;
            minFrame = frame;
        }
    }
    return minFrame;
}

function getLocationFromStack(stack: string | undefined)
{
    if (!stack)
    {
        return undefined;
    }
    const frames = stack.split("\n").filter(frame => frame.indexOf(":") != -1);
    return getLocationFromStackInternal(frames);
}

export function getCallerLocation()
{
    try
    {
        const stack = new Error().stack;
        if (!stack)
        {
            return undefined;
        }
        const frames = stack.split("\n").filter(frame => frame.indexOf(":") != -1);
        const ignore = [];
        if (frames.length < 3) // getCallerLocation, its caller, and their caller which we want to return
        {
            return frames[0];
        }

        // <skyward.something.js?v=1234abcd>:<line>:<column>
        const caller = /(skyward[.\w]+.js[\w=?&%]*):(\d+):\d+/.exec(frames[1]);
        if (caller)
        {
            const [, file, line] = caller;
            if (file.indexOf("bundle.js") !== -1)
            {
                // Bundled files take one line each.  Filter by the filename and line number, and anchor the line number
                // with a trailing ":"
                ignore.push(`${file}:${line}:`);
            }
            else
            {
                // Not bundled, use the filename by itself
                ignore.push(file);
            }
        }
        return getLocationFromStackInternal(frames, ignore);
    }
    catch (e)
    {
        return "";
    }
}

function getQueryParts(query: string)
{
    const entries = query.split(/[?&]/).filter(part => part.length > 0).map(part => part.split("="));
    const result: Dictionary<string, string> = {};
    for (let i = 0; i < entries.length; i++)
    {
        result[entries[i][0]] = entries[i][1];
    }
    return result;
}

/**
 * Refines location to .js/.ts file with row and column numbers
 * 
 * @param location
 * @returns
 */
function refineLocation(location: string | undefined)
{
    let fileAndLocation: string | undefined = location;
    // Regex used to extract URL from string
    const urlRegex = /(https?:\/\/[^\s]+:\d+:\d+)/;
    const match = location?.match(urlRegex);
    if (match)
    {
        fileAndLocation = match[0].substring(match[0].lastIndexOf('/') + 1);
        // Remove query parameters but keep row and column numbers
        if (fileAndLocation.includes('?'))
        {
            const file = fileAndLocation.substring(0, fileAndLocation.indexOf('?'));
            const codeLocation = fileAndLocation.substring(fileAndLocation.indexOf(':'), fileAndLocation.length);
            fileAndLocation = file + codeLocation;
        }
    }

    return fileAndLocation;
}

function reportExceptionInternal(exception: Error, locationArg?: string, applicationMonitoringIdentifierParts?: string[])
{
    const severity = (exception instanceof SkywardException) ? exception.severity : null;

    if (exception.hasOwnProperty("reported"))
    {
        return;
    }

    Object.assign(exception, { reported: true });

    if (severity === Severity.Error || severity === null)
    {
        console.error(exception);
    }
    else
    {
        console.info(exception);
    }

    const location = locationArg ?? getLocationFromStack(exception.stack) ?? "";
    // We want to be able to use this to detect extension errors

    const stack = exception.stack?.toString();
    const details = JSON.stringify(gatherDetails(exception));

    if (shouldIgnoreError(exception.message, location, exception))
    {
        return resolvedPromise(null);
    }

    const thisErrorNumber = errorNumber++;

    const xhr = new XMLHttpRequest();
    let virtualDir = window.skyward?.global?.virtualDir;
    if (virtualDir === undefined)
    {
        virtualDir = "/" + window.location.pathname.split("/")[1] + "/";
    }

    let applicationMonitoringIdentifier = (exception instanceof SkywardException) ? exception.applicationMonitoringIdentifier : undefined;

    // This is a catch all to collect remaining undefined or empty errors and group them together
    if ((!exception.message || exception.message == 'undefined') && applicationMonitoringIdentifier == undefined)
    {
        applicationMonitoringIdentifier = "C3D08DF9-B369-4316-9B73-4E6461BE95AD";
    }

    return withPromise<number | null>(function (resolve, reject)
    {
        const queryParts = getQueryParts(window.location.search);
        let w = window.skyward?.global?.windowGuid ?? queryParts.w;
        let p = window.skyward?.global?.pageStateGuid ?? queryParts.p;
        let sgh = window.skyward?.global?.sessionGuidHash ?? queryParts.sgh;

        xhr.open("POST", `${virtualDir}SkySys/Log/LogClientSideException?w=${w}&p=${p}`);
        xhr.responseType = "json";
        xhr.onload = function ()
        {
            if (typeof this?.response?.id === "number")
            {
                console.log(`Exception logged as id ${this.response.id} with severity ${this.response.severity}`);
            }
            else
            {
                console.log("Exception not logged");
            }
            errorTimes.push(new Date());
            resolve(this.response as number | null);
        };
        xhr.onerror = function (error)
        {
            console.log("Failed to report exception", error);
            reject(error);
        };

        xhr.setRequestHeader("X-CSRF-TOKEN", sgh);
        xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
        var data: Dictionary<string, string> =
        {
            errorNumber: thisErrorNumber.toString(),
            message: exception.message ?? "",
            location,
            stack: stack ?? "",
            details: details,
            severity: severity?.toString(),
            applicationMonitoringIdentifier: applicationMonitoringIdentifier,
            applicationMonitoringIdentifierParts: applicationMonitoringIdentifierParts ? JSON.stringify(applicationMonitoringIdentifierParts) : ""
        };
        var formData = new FormData();
        for (var key in data)
        {
            formData.append(key, escapeForHtml(data[key]!));
        }
        xhr.send(formData);
    });
}

registerErrorDataGatherer("performance", function ()
{
    return {
        pageLoadedAt: new Date(window.performance?.timeOrigin),
        pageLoadComplete: window.performance?.timing?.domComplete - window.performance?.timeOrigin,
        initialNotifyDOMLoadComplete: window.performance?.getEntriesByName?.call(window.performance, "initialNotifyDOMLoadComplete")[0]?.startTime,
        initialLoadComplete: window.performance?.getEntriesByName?.call(window.performance, "initialLoadComplete")[0]?.startTime,
        jsHeapSize: window.performance?.memory?.jsHeapSizeLimit,
        usedJsHeapSize: window.performance?.memory?.usedJsHeapSize
    };
});

registerErrorDataGatherer("exception", function (exception)
{
    return { details: exception.details };
});

registerErrorDataGatherer("AggregateError", function gatherAggregateErrorData(error: any): AggregateErrorData | undefined
{
    if (window.AggregateError && error instanceof window.AggregateError)
    {
        const innerErrors = ((error as AggregateError).errors).map(innerError =>
        {
            const nested = gatherAggregateErrorData(innerError);
            return {
                ...getBasicErrorData(innerError),
                innerErrors: nested,
            };
        });
        return innerErrors;
    }
    return undefined;
});

registerErrorDataGatherer("cause", function (error)
{
    if (error.cause)
    {
        return getBasicErrorData(error.cause);
    }
    return undefined;
});

registerErrorDataGatherer("scripts", function (_error)
{
    const scripts = $("script").toArray()
        .filter(s =>
        {
            const src = (s as HTMLScriptElement).src;
            if (!src)
            {
                return !/\\n\/\/# sourceURL=.*skyward/.test(s.textContent ?? "");
            }
            const host = new URL(src, location.href).host;
            return host !== location.host && host !== "apis.google.com" && host !== "cdn.jsdelivr.net";
        })
        .map(s => s.outerHTML.slice(0, 1000));
    return scripts;
});

if (window.Vue)
{
    const oldCreateApp = window.Vue.createApp;
    (window.Vue as any).createApp = function ()
    {
        const app = oldCreateApp.apply(this, arguments as any);
        const oldErrorHandler = app.config.errorHandler;
        app.config.errorHandler = function (error, _vm, info)
        {
            if (error instanceof Error)
            {
                error.message = `Error during Vue render: ${info}\n${error.message}`;
                reportException(error);
            }
            else
            {
                reportException(new SkywardException(`Error during Vue render: ${info}`, JSON.stringify(error)));
            }

            if (typeof oldErrorHandler === "function")
            {
                (oldErrorHandler as Function).apply(app.config, arguments);
            }
        };
        return app;
    };
}

if (window.jQuery)
{
    const oldExceptionHook = window.jQuery.Deferred.exceptionHook;
    window.jQuery.Deferred.exceptionHook = function (error: any)
    {
        // JQuery calls exceptionHook when it receives an ISE in response to an ajax request with a success callback and no
        // error callback, but we already handle those in ajaxError.js
        if (error !== null && error !== undefined && !error.statusCode)
        {
            if (error instanceof Error)
            {
                error.message = `Uncaught exception in Deferred: ${error.message}`;
                reportException(error);
            }
            else
            {
                reportException(new SkywardException(`Uncaught exception in Deferred: ${error.message}`, JSON.stringify(error)));
            }
        }
        if (typeof oldExceptionHook === "function")
        {
            // If the only argument is the error we just handled, do not continue
            if (arguments.length === 2 && arguments[0] === error)
            {
                return;
            }  
            oldExceptionHook.apply(window.jQuery.Deferred, arguments);
        }
    };
}

function onError(error: ErrorEvent)
{
    if (shouldIgnoreErrorEvent(error))
    {
        return;
    }

    let exception: SkywardException | Error;
    if (error?.error instanceof Error)
    {
        exception = error.error;
        if (error.message)
        {
            exception.message = error.message;
        }
    }
    else
    {
        exception = new SkywardException(error.message, "onError", Severity.Error);
    }

    reportExceptionInternal(exception, `${error.filename}:${error.lineno}:${error.colno}`);
}

function onUnhandledRejection(event: PromiseRejectionEvent)
{
    if (event.reason instanceof CancellationError)
    {
        event.stopPropagation();
        event.preventDefault();
    }
    else if (event.reason instanceof Error)
    {
        const error: Error = event.reason as Error;
        error.message = `Error caught in onUnhandledRejection: ${error.message}`

        reportException(error);
    }
    else
    {
        const message = String(event.reason);
        if (message && message !== 'undefined')
        {
            reportException(new SkywardException(message, "onUnhandledRejection", Severity.Error));
        }
        else
        {
            // This will hopefully help us better determine what causes empty errors ('undefined') to occur in the QDD. We need to group them together to not spam the QDD.
            // We can get rid of this once we hopefully figure out what is causing them.
            reportException(new SkywardException('unhandledrejection event occurred without a specified reason.', `event.reason was empty or undefined.\nevent: ${JSON.stringify(event)}`, Severity.Error, "1F000E6F-DCF1-4AE3-A77E-FD2F1018D4F9"));
        }
    }
}

window.addEventListener("error", onError);
window.addEventListener("unhandledrejection", onUnhandledRejection);

if (!window.skyward)
{
    window.skyward = {} as Skyward;
}

export class CancellationError extends Error
{
    constructor(message?: string | undefined)
    {
        super(message);
    }
}

window.SkywardException = SkywardException;
window.Severity = Severity;

window.skyward.error = window.skyward.error ||
({
    registerErrorDataGatherer,
    reportException,
    getCallerLocation,
    _enableLogging: true,
    CancellationError
} as InternalErrorStatic as SkywardErrorStatic);

(Promise as any)._nativeReject = Promise.reject;
Promise.reject = function (arg)
{
    if (!(arg instanceof Error))
    {
        arg = new SkywardException(`Promise rejection without specific error`, JSON.stringify(arg));
    }
    return (Promise as any)._nativeReject.call(this, arg);
}
